import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { useI18n } from '@/i18n'
import type {
    DeclarationStatus,
    PrincipleGroup,
    Scope,
    Verification,
    VerificationDetail,
    VerificationLevel,
} from '@/types'

/** Verified beats under review beats self-declared. */
const levelOrder: VerificationLevel[] = ['verified', 'under-review', 'self-declared']

export function strongestVerification<T extends Verification>(items: T[]): T | undefined {
    return [...items].sort((a, b) => levelOrder.indexOf(a.level) - levelOrder.indexOf(b.level))[0]
}

function VerificationIcon({ level }: { level: VerificationLevel }) {
    return (
        <svg className="verify__icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.6" />
            {level === 'verified' ? (
                <path
                    d="M4.8 8.3l2.2 2.2 4.2-4.6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.9"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            ) : null}
            {level === 'under-review' ? (
                <path
                    d="M8 4.4V8l2.4 1.8"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            ) : null}
            {level === 'self-declared' ? (
                <circle cx="8" cy="8" r="2.2" fill="currentColor" />
            ) : null}
        </svg>
    )
}

/**
 * Compact "verified by X" marker. `compact` shows only the verifier, with
 * the full sentence in the tooltip — for cards and dense lists.
 */
export function VerificationBadge({
    verification,
    verifier,
    compact,
    extra,
}: {
    verification: Verification
    verifier?: string
    compact?: boolean
    extra?: number
}) {
    const { t, formatDate } = useI18n()
    const name = verifier ?? verification.verifier
    const label = t(`verification.by.${verification.level}`).replace('{name}', name)
    return (
        <span
            className={`verify verify--${verification.level}`}
            title={`${label} · ${formatDate(verification.date)}`}
        >
            <VerificationIcon level={verification.level} />
            {label}
            {extra ? ` +${extra}` : ''}
            {compact ? null : <span className="verify__date">{formatDate(verification.date)}</span>}
        </span>
    )
}

/** The whole verification history of a resource or a profile. */
export function VerificationList({ items }: { items: VerificationDetail[] }) {
    const { t, localized } = useI18n()
    if (!items.length) {
        return <p className="empty">{t('verification.none')}</p>
    }
    return (
        <div className="stack">
            {items.map((item, index) => (
                <div className="usage" key={`${item.verifier}-${index}`}>
                    <div className="usage__head">
                        <VerificationBadge
                            verification={item}
                            verifier={
                                item.verifier_detail
                                    ? localized(item.verifier_detail.name)
                                    : item.verifier
                            }
                        />
                        {item.url ? (
                            <a href={item.url} target="_blank" rel="noreferrer noopener">
                                {t('verification.record')}
                            </a>
                        ) : null}
                    </div>
                    {item.note ? <p className="declaration__note">{localized(item.note)}</p> : null}
                </div>
            ))}
        </div>
    )
}

/** Page body inside the 1240px container. */
export function PageShell({ children }: { children: ReactNode }) {
    return <div className="shell content__body">{children}</div>
}

/** Full-width teal opening band, as used on the EOSC CZ services. */
export function Hero({
    eyebrow,
    title,
    lead,
    actions,
}: {
    eyebrow?: string
    title: string
    lead?: string
    actions?: ReactNode
}) {
    return (
        <section className="hero">
            <div className="shell">
                {eyebrow ? <p className="hero__eyebrow">{eyebrow}</p> : null}
                <h1>{title}</h1>
                {lead ? <p className="hero__lead">{lead}</p> : null}
                {actions ? <div className="hero__actions">{actions}</div> : null}
            </div>
        </section>
    )
}

/** Heading block for the inner pages. */
export function PageHead({ title, lead }: { title: string; lead?: string }) {
    return (
        <div className="pagehead">
            <h1>{title}</h1>
            {lead ? <p className="lead">{lead}</p> : null}
        </div>
    )
}

export function Chip({
    children,
    variant,
    title,
}: {
    children: ReactNode
    variant?: string
    title?: string
}) {
    return (
        <span className={`chip${variant ? ` chip--${variant}` : ''}`} title={title}>
            {children}
        </span>
    )
}

export function StatusChip({ status }: { status: DeclarationStatus }) {
    const { statusName } = useI18n()
    return <Chip variant={status}>{statusName(status)}</Chip>
}

export function ScopeChip({ scope }: { scope: Scope }) {
    const { scopeName } = useI18n()
    return <Chip variant="outline">{scopeName(scope)}</Chip>
}

export function PrincipleChip({ id, group }: { id: string; group?: PrincipleGroup }) {
    const derived = (group ?? (id[0] as PrincipleGroup)) || 'F'
    return (
        <span className={`chip chip--principle chip--${derived}`} title={id}>
            {id}
        </span>
    )
}

export function StatCard({ value, label }: { value: number | string; label: string }) {
    return (
        <div className="card stat">
            <span className="stat__value">{value}</span>
            <span className="stat__label">{label}</span>
        </div>
    )
}

export function Bar({
    label,
    value,
    max,
    caption,
    group,
    wide,
}: {
    label: string
    value: number
    max: number
    caption?: string
    group?: PrincipleGroup
    wide?: boolean
}) {
    const percent = max > 0 ? Math.round((value / max) * 100) : 0
    return (
        <div className={`bar bar--${group ?? 'neutral'}${wide ? ' bar--wide' : ''}`}>
            <span className="bar__label" title={label}>
                {label}
            </span>
            <span className="bar__track">
                <span className="bar__fill" style={{ width: `${Math.max(percent, 2)}%` }} />
            </span>
            <span className="bar__value">{caption ?? value}</span>
        </div>
    )
}

export function Section({
    title,
    hint,
    action,
    children,
}: {
    title: string
    hint?: string
    action?: ReactNode
    children: ReactNode
}) {
    return (
        <section className="section">
            <div className="section__head">
                <div>
                    <h2>{title}</h2>
                    {hint ? <p className="section__hint">{hint}</p> : null}
                </div>
                {action}
            </div>
            {children}
        </section>
    )
}

export function Loading() {
    const { t } = useI18n()
    return (
        <p className="state" role="status">
            {t('common.loading')}
        </p>
    )
}

export function ErrorState({ error, onRetry }: { error: Error; onRetry?: () => void }) {
    const { t } = useI18n()
    return (
        <div className="state state--error" role="alert">
            <p>
                <strong>{t('common.error')}</strong>
            </p>
            <p>{t('common.errorHint')}</p>
            <p className="item__meta">{error.message}</p>
            {onRetry ? (
                <button className="button" onClick={onRetry} type="button">
                    {t('common.retry')}
                </button>
            ) : null}
        </div>
    )
}

export function BackLink({ to, label }: { to: string; label: string }) {
    return (
        <Link className="detail__back" to={to}>
            ← {label}
        </Link>
    )
}

export function DefinitionItem({ term, children }: { term: string; children: ReactNode }) {
    return (
        <div>
            <dt>{term}</dt>
            <dd>{children}</dd>
        </div>
    )
}
